// src/pages/DriversManagement.js

import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';

function DriversManagement() {
  const { userProfile, loading: authLoading } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [newDriverName, setNewDriverName] = useState('');
  const [newDriverPhone, setNewDriverPhone] = useState(''); // Estado para el teléfono
  const [editingDriverId, setEditingDriverId] = useState(null);
  const [editingDriverName, setEditingDriverName] = useState('');
  const [editingDriverPhone, setEditingDriverPhone] = useState(''); // Estado para el teléfono en edición
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const driversCollectionRef = collection(db, 'conductores');

  const getDrivers = async () => {
    setError('');
    try {
      const data = await getDocs(driversCollectionRef);
      setDrivers(data.docs.map(doc => ({ ...doc.data(), id: doc.id })));
    } catch (err) {
      console.error("Error al obtener conductores:", err.message);
      setError("No se pudieron cargar los conductores.");
    }
  };

  useEffect(() => {
    if (!authLoading && userProfile && userProfile.rol === 'Administrador') {
      getDrivers();
    }
  }, [authLoading, userProfile]);

  const addDriver = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!newDriverName.trim() || !newDriverPhone.trim()) { // Ambos campos obligatorios
      setError('El nombre del conductor y el teléfono son obligatorios.');
      return;
    }
    if (!/^\d{9}$/.test(newDriverPhone)) { // Validación simple de 9 dígitos para teléfono
        setError('El teléfono del conductor debe ser de 9 dígitos numéricos.');
        return;
    }

    try {
      await addDoc(driversCollectionRef, {
        name: newDriverName.toUpperCase(), // Guardar en mayúsculas
        phone: newDriverPhone, // Guardar el teléfono
      });
      setSuccess('Conductor añadido exitosamente!');
      setNewDriverName('');
      setNewDriverPhone('');
      getDrivers();
    } catch (err) {
      console.error("Error al añadir conductor:", err.message);
      setError("No se pudo añadir el conductor.");
    }
  };

  const startEdit = (driver) => {
    setEditingDriverId(driver.id);
    setEditingDriverName(driver.name);
    setEditingDriverPhone(driver.phone || ''); // Asegurar que el campo exista
  };

  const cancelEdit = () => {
    setEditingDriverId(null);
    setEditingDriverName('');
    setEditingDriverPhone('');
  };

  const updateDriver = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!editingDriverName.trim() || !editingDriverPhone.trim()) {
      setError('El nombre del conductor y el teléfono son obligatorios.');
      return;
    }
    if (!/^\d{9}$/.test(editingDriverPhone)) {
        setError('El teléfono del conductor debe ser de 9 dígitos numéricos.');
        return;
    }

    const driverDoc = doc(db, 'conductores', editingDriverId);
    try {
      await updateDoc(driverDoc, {
        name: editingDriverName.toUpperCase(), // Guardar en mayúsculas
        phone: editingDriverPhone, // Actualizar el teléfono
      });
      setSuccess('Conductor actualizado exitosamente!');
      cancelEdit();
      getDrivers();
    } catch (err) {
      console.error("Error al actualizar conductor:", err.message);
      setError("No se pudo actualizar el conductor.");
    }
  };

  const deleteDriver = async (id) => {
    setError('');
    if (window.confirm('¿Estás seguro de que quieres eliminar este conductor?')) {
      const driverDoc = doc(db, 'conductores', id);
      try {
        await deleteDoc(driverDoc);
        setSuccess('Conductor eliminado exitosamente!');
        getDrivers();
      } catch (err) {
        console.error("Error al eliminar conductor:", err.message);
        setError("No se pudo eliminar el conductor.");
      }
    }
  };

  const canManageDrivers = userProfile && userProfile.rol === 'Administrador';

  if (!canManageDrivers) {
    return (
      <div style={{ textAlign: 'center', padding: '50px', color: 'red' }} className="app-content-container">
        <h3>Acceso Denegado</h3>
        <p>No tienes los permisos necesarios para gestionar conductores.</p>
      </div>
    );
  }

  return (
    <div className="app-content-container">
      <h2 style={{ textAlign: 'center' }}>Gestión de Conductores</h2>

      {error && <p className="login-error">{error}</p>}
      {success && <p style={{ color: 'green', backgroundColor: '#e6ffe6', border: '1px solid green', padding: '10px', borderRadius: '5px', marginBottom: '20px', textAlign: 'center' }}>{success}</p>}

      {/* Añadir Nuevo Conductor */}
      <div style={{ marginBottom: '40px', padding: '20px', border: '1px solid #e0e0e0', borderRadius: '8px', backgroundColor: '#f9f9f9', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
        <h3 style={{ marginTop: '0', color: 'var(--secondary-blue)' }}>Añadir Nuevo Conductor</h3>
        <form onSubmit={addDriver} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          <div className="login-form-group">
            <label htmlFor="newDriverName">Nombre del Conductor:</label>
            <input
              type="text"
              id="newDriverName"
              placeholder="Nombre Completo"
              value={newDriverName}
              onChange={(e) => setNewDriverName(e.target.value)}
              required
            />
          </div>
          <div className="login-form-group">
            <label htmlFor="newDriverPhone">Teléfono:</label>
            <input
              type="tel"
              id="newDriverPhone"
              placeholder="Ej: 987654321"
              value={newDriverPhone}
              onChange={(e) => setNewDriverPhone(e.target.value)}
              maxLength={9}
              pattern="\d{9}"
              title="Debe ser un número de 9 dígitos"
              required
            />
          </div>
          <button type="submit" style={{ backgroundColor: 'var(--secondary-blue)', color: 'white', padding: '10px 20px' }}>
            Añadir Conductor
          </button>
        </form>
      </div>

      {/* Lista de Conductores */}
      <h3 style={{ color: 'var(--secondary-blue)', marginBottom: '20px' }}>Lista de Conductores</h3>
      {drivers.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#666' }}>No hay conductores registrados.</p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Teléfono</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {drivers.map(driver => (
              <tr key={driver.id}>
                <td data-label="Nombre">
                  {editingDriverId === driver.id ? (
                    <input
                      type="text"
                      value={editingDriverName}
                      onChange={(e) => setEditingDriverName(e.target.value)}
                      style={{ width: '100%', padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
                    />
                  ) : (
                    driver.name
                  )}
                </td>
                <td data-label="Teléfono">
                  {editingDriverId === driver.id ? (
                    <input
                      type="tel"
                      value={editingDriverPhone}
                      onChange={(e) => setEditingDriverPhone(e.target.value)}
                      maxLength={9}
                      pattern="\d{9}"
                      title="Debe ser un número de 9 dígitos"
                      style={{ width: '100%', padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
                    />
                  ) : (
                    driver.phone || 'N/A' // Mostrar N/A si no hay teléfono
                  )}
                </td>
                <td data-label="Acciones" className="action-buttons">
                  {editingDriverId === driver.id ? (
                    <>
                      <button onClick={updateDriver} style={{ backgroundColor: '#28A745', color: 'white' }}>Guardar</button>
                      <button onClick={cancelEdit} style={{ backgroundColor: '#6C757D', color: 'white' }}>Cancelar</button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => startEdit(driver)} style={{ backgroundColor: '#FFC107', color: 'white' }}>Editar</button>
                      <button onClick={() => deleteDriver(driver.id)} style={{ backgroundColor: 'var(--primary-red)', color: 'white' }}>Eliminar</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default DriversManagement;